/**
 * PITH <ds-variant-select> — the mobile-only dropdown view over Horizon's
 * variant-picker radios (see snippets/ds-variant-dropdown.liquid).
 *
 * The dropdown owns no state. Picking an option finds the matching radio inside
 * the sibling fieldset[data-fieldset-index] and fires its native change event,
 * so <variant-picker> (assets/variant-picker.js) runs its normal flow: update
 * URL, price, media, then morph the section. The morph re-renders this element
 * server-side with the new selected value, so there is nothing to sync back.
 *
 * Custom elements auto-upgrade after a morph, so a plain define() is enough — no
 * MutationObserver, no re-init hooks.
 */
if (!customElements.get('ds-variant-select')) {
  class DsVariantSelect extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('.ds-vsel__d');
      this.panel = this.querySelector('.ds-vsel__panel');
      if (!this.details || !this.panel) return;

      this.onPanelClick = this.onPanelClick.bind(this);
      this.onDocPointer = this.onDocPointer.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.panel.addEventListener('click', this.onPanelClick);
      this.details.addEventListener('toggle', () => {
        if (this.details.open) {
          document.addEventListener('pointerdown', this.onDocPointer, true);
          this.addEventListener('keydown', this.onKeydown);
          const selected = this.panel.querySelector('.ds-vsel__opt.is-selected') || this.panel.querySelector('.ds-vsel__opt');
          selected?.focus({ preventScroll: true });
        } else {
          document.removeEventListener('pointerdown', this.onDocPointer, true);
          this.removeEventListener('keydown', this.onKeydown);
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocPointer, true);
    }

    /** @returns {HTMLFieldSetElement | null} */
    get fieldset() {
      const index = this.dataset.fieldsetIndex;
      const form = this.closest('.variant-picker__form');
      return form ? form.querySelector(`fieldset[data-fieldset-index="${index}"]`) : null;
    }

    onPanelClick(event) {
      const opt = event.target.closest('[data-ds-vsel-opt]');
      if (!opt) return;
      event.preventDefault();
      this.select(opt.dataset.value);
    }

    /** @param {string} value */
    select(value) {
      const fieldset = this.fieldset;
      if (!fieldset) return;
      const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
      this.details.open = false;
      this.querySelector('.ds-vsel__trigger')?.focus({ preventScroll: true });
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('input', { bubbles: true }));
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onDocPointer(event) {
      if (!this.contains(event.target)) this.details.open = false;
    }

    onKeydown(event) {
      const opts = Array.from(this.panel.querySelectorAll('[data-ds-vsel-opt]'));
      const current = document.activeElement;
      const i = opts.indexOf(current);

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.details.open = false;
          this.querySelector('.ds-vsel__trigger')?.focus({ preventScroll: true });
          break;
        case 'ArrowDown':
          event.preventDefault();
          opts[Math.min(i + 1, opts.length - 1)]?.focus({ preventScroll: true });
          break;
        case 'ArrowUp':
          event.preventDefault();
          (i <= 0 ? opts[0] : opts[i - 1])?.focus({ preventScroll: true });
          break;
        case 'Home':
          event.preventDefault();
          opts[0]?.focus({ preventScroll: true });
          break;
        case 'End':
          event.preventDefault();
          opts[opts.length - 1]?.focus({ preventScroll: true });
          break;
      }
    }
  }

  customElements.define('ds-variant-select', DsVariantSelect);
}
